// packages/permissions-ui/src/failure-copy.ts
//
// ENG-2539 — the one shared three-step ladder for "what does a caller show
// when a mutation or query fails", ratified in
// docs/architecture-decisions/ENG-2539-operator-error-copy.md:
//
//   1. A Cedar/RBAC `Forbidden` or `StepUpRequired` deny, via deny.ts. Tried
//      FIRST and not skippable — `deny` is not an injected option, because
//      "authz always wins" is the invariant this function exists to make
//      unbreakable.
//   2. The caller's own domain composer, for a recognized business-rule
//      shape (a typed `Conflict`, a validation payload, ...).
//   3. A stable, injected generic sentence.
//
// Never `.message`, never `String(err)`. The ladder itself never reads
// either; a `compose` that does is a caller bug this module cannot stop — see
// the ADR's "compose is caller-trusted" section.
//
// The census (scripts/error-copy-census.ts) catches SOME of those caller bugs,
// and it is worth being exact about which, because "the census flags it" was
// doing more work in this comment than the script can bear. It recognizes
// `param.message` and `String(param)` written directly inside a `compose`
// function literal. It does not recognize `param['message']`, a destructured
// `({ message }) =>`, a template `` `${param}` ``, `param.toString()`, or a
// named helper passed as `compose` — each of those reads the server's message
// and scans clean. Treat the census as a ratchet against the common shape, not
// as proof that no caller launders a message.
//
// `apps/main`, `apps/admin`, and `apps/portal` each grew their own copy of
// this ladder (mutation-failure-copy.ts, pipelineLoadErrorBanner.ts,
// portalViewEditorState.ts's safeCorrelatedMessage, publishBlockerMessage.ts)
// with the invariants enforced only by review in each one. This is the
// single tested version.
//
// AUDIENCE NARROWING (ENG-2539, see the ADR's "customer audience" section)
// --------------------------------------------------------------------------
// `denyMessageFromError`'s output is not unconditionally safe for a customer
// screen: a permission key the generated registry hasn't caught up to (every
// `molaris.*` control-plane key, today) falls back to a label DERIVED from
// the key text, not a curated one, and a lifecycle-locked Forbidden renders
// apps/main's internal publishing-workflow copy. For `audience: 'customer'`
// this module suppresses the deny rung — falling through to `compose` then
// `generic` — whenever the matched candidate is a lifecycle Forbidden, or
// names an `action` or `permissionKey` the registry does not recognize. The
// narrowing runs on the EXACT extensions object `matchedDenyExtensions`
// selected (deny.ts's own walk), never a second one, so the predicate and
// the sentence can never describe different candidates. `audience:
// 'internal'` applies no narrowing — apps/main and apps/admin see exactly
// what `denyMessageFromError` has always returned.

import {
  denyMessageFromExtensions,
  isLifecycleForbidden,
  matchedDenyExtensions,
  type DenyExtensions,
} from './deny.js';
import { isKnownPermissionKey } from './labels.js';

/** `'internal'` — apps/main, apps/admin: no narrowing beyond deny.ts's own.
 *  `'customer'` — apps/portal: additionally suppresses a deny sentence this
 *  module cannot vouch for as customer-safe (see the module docblock). */
export type FailureCopyAudience = 'internal' | 'customer';

export interface FailureCopyOptions {
  /** Which narrowing policy applies to the deny rung. Required, and not
   *  defaulted — a caller must say which boundary it renders across. */
  readonly audience: FailureCopyAudience;
  /**
   * The stable rung-3 sentence, or `null` when this call site has nothing
   * to show for an unrecognized failure (the caller renders no description
   * node at all rather than a placeholder — see
   * apps/portal/src/lib/portal-failure-copy.ts's retry sentence for the
   * alternative, a subject-less sentence passed as `generic` instead).
   */
  readonly generic: string | null;
  /**
   * Caller-trusted domain recognizer, tried after the deny rung and before
   * `generic`. MUST build its return value only from typed `extensions` —
   * never from `err.message` or `String(err)`. Returns `null` or `undefined`
   * for anything it does not recognize, which falls through to `generic`.
   * See the module docblock's "never .message" note: the census flags a
   * `.message` read inside a `compose` function literal, but cannot follow
   * one laundered through a variable or a helper — that limit is on review,
   * not the machine.
   */
  readonly compose?: (err: unknown) => string | null | undefined;
  /** Support correlation id. Appended, in deny.ts's own
   *  `(Reference: ...)` format, to the deny sentence and to `generic` —
   *  never to `compose`'s output, which owns its own wording. Omit it and
   *  neither carries a reference. */
  readonly correlationId?: string | null;
  /**
   * Invoked exactly once per call, with the ORIGINAL `err` value, before
   * this function returns — regardless of which rung produced the result.
   * The one place a caller can log the real error (console, an error
   * reporter) without every call site having to remember to. See
   * apps/portal/src/lib/portal-failure-copy.ts, whose bound helper wires
   * this to `console.error` once for the whole app.
   */
  readonly onError?: (err: unknown) => void;
}

function withReference(sentence: string, correlationId?: string | null): string {
  return correlationId ? `${sentence} (Reference: ${correlationId})` : sentence;
}

/**
 * Does this string put anything on screen?
 *
 * The rung-2 check used to be `composed.length > 0`, which admitted `"   "`,
 * `"\n"` and a lone zero-width space — each rendered verbatim as a description
 * node with nothing visible in it, the placeholder the `generic: null`
 * contract exists to avoid. A blank result means `compose` did not recognize
 * the shape, so it must fall through to `generic` like any other miss.
 *
 * `trim()` alone is not enough and the repo already knows why: as
 * apps/main/src/lib/mutation-failure-copy.ts's `sanitizeName` records, the
 * `\p{Cf}` format class is zero-width by definition and is NOT matched by
 * `\s`. U+200B and U+FEFF survive `trim()`; so do the bidi overrides
 * U+202A-U+202E, which are worse than invisible because they reorder the
 * rendering of whatever follows them.
 */
function hasVisibleText(value: string): boolean {
  for (const ch of value) {
    if (/\s/.test(ch)) continue;
    if (/\p{Cf}/u.test(ch)) continue;
    return true;
  }
  return false;
}

/**
 * `audience: 'customer'` suppression predicate — see the module docblock.
 * Evaluated on the SAME `DenyExtensions` object the sentence would be built
 * from, so it can never narrow a different candidate than the one that
 * matched.
 */
function customerAudienceSuppresses(ext: DenyExtensions): boolean {
  if (isLifecycleForbidden(ext)) return true;
  const action = typeof ext.action === 'string' ? ext.action : null;
  if (action !== null && action !== '' && !isKnownPermissionKey(action)) return true;
  const permissionKey = typeof ext.permissionKey === 'string' ? ext.permissionKey : null;
  if (permissionKey !== null && permissionKey !== '' && !isKnownPermissionKey(permissionKey)) {
    return true;
  }
  return false;
}

/**
 * Decide what a failed mutation or query shows, per the ladder in the module
 * docblock. Returns `null` only when `generic` is `null` and nothing above it
 * fired — never `'[object Object]'`, `'null'`, or `'undefined'`, because
 * nothing in this ladder ever stringifies `err`.
 */
export function failureCopy(err: unknown, options: FailureCopyOptions): string | null {
  const { audience, generic, compose, correlationId, onError } = options;

  // Exactly once, with the original value, before any rung below returns —
  // regardless of outcome. See FailureCopyOptions.onError.
  onError?.(err);

  const ext = matchedDenyExtensions(err);
  if (ext !== null && !(audience === 'customer' && customerAudienceSuppresses(ext))) {
    const denied = denyMessageFromExtensions(ext, correlationId);
    if (denied !== null) return denied;
  }

  const composed = compose?.(err);
  if (typeof composed === 'string' && hasVisibleText(composed)) return composed;

  return generic === null ? null : withReference(generic, correlationId);
}
